import { useEffect, useState, useRef } from 'react';
import { SignalBars } from '../../components/SignalBars';
import type { Map as LeafletMap } from 'leaflet';
import { useHttpClient } from '../../../shared/http-client';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { PageBody } from '../../../elements/page-body';

// ...otros imports

interface Vehicle {
    id: number;
    lat: number;
    lng: number;
    name: string;
}

const latlng = new L.LatLng(40.7128, -74.0060);


const formatFuelValue = (value: any) => {
    if (value === undefined || value === null) return '0.00';
    const numericValue = Number(value);
    if (isNaN(numericValue)) return '0.00';
    return (numericValue / 1000).toFixed(2);
};

export const HomeBody = () => {
    const [selectedDevice, setSelectedDevice] = useState<any>(null);


    const { t } = useTranslation() as { t: (key: string) => string };
    const { api, state } = useHttpClient();
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [allDevices, setAllDevices] = useState<any[]>([]);
    const [bounds, setBounds] = useState<[[number, number], [number, number]] | undefined>(undefined);

    // Sincroniza selectedDevice con los datos en tiempo real de allDevices
    useEffect(() => {
        if (!selectedDevice) return;
        const updated = allDevices.find(
            d => d.vehicle?.id === selectedDevice.vehicle?.id
        );
        if (updated && updated !== selectedDevice) {
            setSelectedDevice(updated);
        }
    }, [allDevices, selectedDevice]);

    useEffect(() => {
        const ws = new WebSocket(process.env.REACT_APP_WS + "tracker-devices/");

        ws.onopen = () => {
            console.log('WebSocket connected');
        };

        ws.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                if (message.devices && Array.isArray(message.devices)) {
                    setAllDevices(message.devices);
                    const newPositions: Vehicle[] = message.devices
                        .filter((device: any) => device.vehicle && device.data && device.data.gps)
                        .map((device: any) => ({
                            id: device.vehicle.id,
                            lat: device.data.gps.latitude,
                            lng: device.data.gps.longitude,
                            name: device.vehicle.plate,
                        }));

                    setVehicles(prevVehicles => {
                        const updatedVehicles = [...prevVehicles];
                        newPositions.forEach(newPosition => {
                            const existingVehicleIndex = updatedVehicles.findIndex(v => v.id === newPosition.id);
                            if (existingVehicleIndex > -1) {
                                updatedVehicles[existingVehicleIndex] = newPosition;
                            } else {
                                updatedVehicles.push(newPosition);
                            }
                        });
                        return updatedVehicles;
                    });
                }
            } catch (error) {
                console.error('Error parsing WebSocket message:', error);
            }
        };

        ws.onerror = (error) => {
            console.error('WebSocket error:', error);
            toast.error('Realtime connection error');
        };

        ws.onclose = () => {
            console.log('WebSocket disconnected');
        };

        return () => {
            ws.close();
        };
    }, []);

    useEffect(() => {
        if (vehicles.length > 0) {
            const lats = vehicles.map(v => v.lat);
            const lngs = vehicles.map(v => v.lng);
            const minLat = Math.min(...lats);
            const maxLat = Math.max(...lats);
            const minLng = Math.min(...lngs);
            const maxLng = Math.max(...lngs);
            setBounds([[minLat, minLng], [maxLat, maxLng]]);
        }
    }, [vehicles]);

    useEffect(() => {
        if (state.error) {
            toast.error(state.error.detail);
        }
    }, [state]);

    const [boundsFitted, setBoundsFitted] = useState(false);
    useEffect(() => {
        setBoundsFitted(false);
    }, [vehicles.length]);

    const [userInteracted, setUserInteracted] = useState(false);

    const mapRef = useRef<LeafletMap | null>(null);
    const hasFittedRef = useRef(false);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        const onMove = () => setUserInteracted(true);
        map.on('zoomstart', onMove);
        map.on('movestart', onMove);
        return () => {
            map.off('zoomstart', onMove);
            map.off('movestart', onMove);
        };
    }, []);



    const [activeGrayPopup, setActiveGrayPopup] = useState<string | number | null>(null);
    const devicesNoData = allDevices.filter((device: any) =>
        !device.data || !device.data.gps && (device.lastLat === undefined || device.lastLng === undefined)
    );
    // El resto sigue como marcadores

    // Cerrar popup al hacer clic fuera
    useEffect(() => {
        if (!activeGrayPopup) return;
        const handler = (e: MouseEvent) => {
            // Solo cierra si el click no es dentro del overlay
            const popup = document.getElementById('gray-popup-overlay');
            if (popup && !popup.contains(e.target as Node)) {
                setActiveGrayPopup(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [activeGrayPopup]);

    // Cálculo de totales para leyenda
    const totalActivos = allDevices.filter((d: any) => d.data && d.data.io_elements).length;
    let totalVerdes = 0, totalRojos = 0;
    allDevices.forEach((device: any) => {
        if (device.data && device.data.io_elements) {
            const isMove = device.data.io_elements?.[240].value;
            if (isMove == "1") {
                totalVerdes++;
            } else {
                totalRojos++;
            }
            /* if (device.data && device.data.gps) {
                let lat = device.data.gps.latitude;
                let lng = device.data.gps.longitude;
                if (device.lastLat !== undefined && device.lastLng !== undefined) {
                    if (device.lastLat !== lat || device.lastLng !== lng) {
                        totalVerdes++;
                    } else {
                        totalRojos++;
                    }
                } else {
                    totalRojos++;
                }
            } */
        }
    });
    const totalGrises = devicesNoData.length;

    // Estado para filtro de placa
    const [selectedPlate, setSelectedPlate] = useState<string>('ALL');
    // Lista de placas únicas
    const allPlates = Array.from(new Set(allDevices.map((d: any) => d.vehicle?.plate).filter(Boolean)));
    // Filtrar dispositivos según placa seleccionada
    const filteredDevices = selectedPlate === 'ALL'
        ? allDevices
        : allDevices.filter((d: any) => d.vehicle?.plate === selectedPlate);
    // Filtrar overlays grises también
    const filteredDevicesNoData = filteredDevices.filter((device: any) =>
        !device.data || !device.data.gps && (device.lastLat === undefined || device.lastLng === undefined)
    );

    const defaultCenter: [number, number] = [43.509865, -0.118092];
    const [initialCenter, setInitialCenter] = useState<[number, number]>(defaultCenter);
    const [hasSetCenter, setHasSetCenter] = useState(false);

    const firstDeviceWithData = filteredDevices.find(
        (device: any) =>
            device.data &&
            device.data.gps
    );

    if (firstDeviceWithData?.data?.gps?.latitude && !hasSetCenter) {
        setInitialCenter([
            +firstDeviceWithData.data.gps.latitude,
            +firstDeviceWithData.data.gps.longitude
        ]);
        setHasSetCenter(true)
    }


    return (
        <PageBody title={'common.label.mapa-tiempo-real'}>
            <div className="w-full ">
                {/* Select de placas fuera del mapa */}
                <div className="mb-4 mt-2">
                    <select
                        value={selectedPlate}
                        onChange={e => setSelectedPlate(e.target.value)}
                        className="text-[13px] px-2 py-1.5 rounded border border-gray-400 min-w-[120px]" >
                        <option value="ALL">{t('common.label.todos-los-vehiculos')}</option>
                        {allPlates.map((plate: string) => (
                            <option key={plate} value={plate}>{plate}</option>
                        ))}
                    </select>
                </div>
                {selectedDevice && (
                    <div className="bg-white rounded shadow-lg p-4 border border-gray-200">
                        <div className='flex justify-between'>
                            <div className='text-[13px]'><span className="font-semibold  text-[13px]">{t('common.form-label.matricula')}:</span> {selectedDevice.vehicle?.plate ?? ''}</div>
                            <div>
                                <button
                                    className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm"
                                    onClick={() => setSelectedDevice(null)}
                                >
                                    {t('common.label.close')}
                                </button>
                            </div>
                        </div>

                        <div className='flex'>
                            <div>
                                <div className='text-[13px]'><span className="font-semibold  text-[13px]">{t('common.label.velocidad')}:</span> {selectedDevice?.data?.io_elements?.[37]?.value} {selectedDevice?.data?.io_elements?.[37]?.value ? `Km/h` : '0 Km/h'}</div>
                                <div className='text-[13px]'><span className="font-semibold  text-[13px]">{t('common.label.combustible-usado')}:</span> {formatFuelValue(selectedDevice?.data?.io_elements?.[12]?.value)} L</div>
                                <div className='text-[13px]'><span className="font-semibold  text-[13px]">{t('common.label.litros-por-hora')}:</span> {formatFuelValue(selectedDevice?.data?.io_elements?.[13]?.value)} L</div>
                            </div>
                            <div className='pl-8'>
                                <div className='text-[13px]'><span className="font-semibold  text-[13px]">{t('common.label.motor-encendido')}:</span> {selectedDevice?.data?.io_elements?.[239]?.value == "0" ? t('common.label.no') : t('common.label.yes')}</div>
                                <div className='text-[13px] flex items-center gap-2 pt-2'>
                                    <span className="font-semibold text-[13px] pt-[12px]">{t('common.label.senal-gsm')}:</span>
                                    <SignalBars value={Number(selectedDevice?.data?.io_elements?.[21]?.value) || 0} />
                                </div>
                            </div>
                            <div className='pl-8'>
                                <div className='text-[13px]'><span className="font-semibold  text-[13px]">{t('common.label.nivel-combustible')}:</span> {selectedDevice?.data?.io_elements?.[48]?.value}  {selectedDevice?.data?.io_elements?.[48]?.value ? '%' : ''}</div>
                                <div className='text-[13px]'><span className="font-semibold  text-[13px]">{t('Eco score')}:</span> {selectedDevice?.data?.io_elements?.[15]?.value}</div>
                                <div className='text-[13px]'><span className="font-semibold  text-[13px]">{t('common.label.rpm-motor')}:</span> {selectedDevice?.data?.io_elements?.[36]?.value} {selectedDevice?.data?.io_elements?.[36]?.value ? '' : '0'}</div>
                            </div>
                            <div className='pl-8'>
                                <div className='text-[13px]'><span className="font-semibold  text-[13px]">{t('common.label.metros-recorridos')}:</span> {selectedDevice?.data?.io_elements?.[199]?.value}</div>
                                {/* <div className='text-[13px]'><span className="font-semibold  text-[13px]">{t('Eco score')}:</span> {selectedDevice?.data?.io_elements?.[15]?.value}</div>
                            <div className='text-[13px]'><span className="font-semibold  text-[13px]">{t('common.label.rpm-motor')}:</span> {selectedDevice?.data?.io_elements?.[36]?.value}</div> */}
                            </div>
                        </div>




                    </div>
                )}
                <div className="relative w-full" style={{ height: 'calc(100vh - 140px)' }}>


                    {/* Leyenda arriba a la derecha */}
                    <div className="absolute top-[18px] right-[18px] bg-white/95 border border-gray-200 rounded-lg shadow-md px-5 py-2.5 z-[2001] text-[13px] min-w-[220px]">
                        <div className="flex items-center gap-2 pb-2">
                            <span>{t('common.label.vehiculos-activos')}:</span>
                            <b style={{ marginLeft: 4 }}>{totalActivos}</b>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span className="inline-block w-[14px] h-[14px] rounded-full bg-[#b9fbc0] border border-[1.5px] border-black mr-1.5"></span>
                            <span>{t('common.label.en-marcha')}:</span>
                            <b style={{ marginLeft: 4 }}>{totalVerdes}</b>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span className="inline-block w-[14px] h-[14px] rounded-full bg-[#ffb3b3] border border-[1.5px] border-black mr-1.5"></span>
                            <span>{t('common.label.parados')}:</span>
                            <b style={{ marginLeft: 4 }}>{totalRojos}</b>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span className="inline-block w-[14px] h-[14px] rounded-full bg-[#e0e0e0] border border-[1.5px] border-black mr-1.5"></span>
                            <span>{t('common.label.sin-datos-devices')}:</span>
                            <b style={{ marginLeft: 4 }}>{totalGrises}</b>
                        </div>
                    </div>
                    {/* Overlay para vehículos sin data y sin última posición */}
                    {filteredDevicesNoData.length > 0 && (
                        <div className="absolute top-[100px] left-3 z-[1000] flex flex-col gap-1">
                            {filteredDevicesNoData.map((device: any, idx: number) => {
                                const key = device.vehicle?.id ?? idx;
                                return (
                                    <div key={key} className="text-[10px] font-semibold bg-[#e0e0e0] rounded px-1.5 py-0.5 border border-black cursor-pointer whitespace-nowrap inline-block mb-0.5 relative"
                                        onClick={() => setActiveGrayPopup(activeGrayPopup === key ? null : key)}
                                    >
                                        {device.vehicle?.plate ?? 'Sin placa'}
                                        {activeGrayPopup === key && (
                                            <div
                                                id="gray-popup-overlay"
                                                className="absolute left-full top-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg p-2 min-w-[120px] z-[2000] cursor-text text-center font-semibold text-[12px]"
                                                onClick={e => e.stopPropagation()}
                                            >
                                                <button
                                                    onClick={() => setActiveGrayPopup(null)}
                                                    className="absolute top-1 right-1.5 border-none bg-transparent text-[16px] font-light cursor-pointer text-gray-500 leading-none"
                                                    aria-label="Cerrar"
                                                >
                                                    x
                                                </button>
                                                <div className="font-semibold text-[14px] mt-2">{device.vehicle?.plate ?? ''}</div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {/* Centro dinámico: si hay device con datos, centrarse en él, si no usar el centro por defecto */}


                    <MapContainer
                        center={initialCenter}
                        zoom={5}
                        whenReady={() => { }}
                        style={{ height: '100%', width: '100%' }}
                    >


                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {filteredDevices.map((device: any, index: number) => {
                            let lat: number | undefined, lng: number | undefined;
                            const hasData = !!(device.data && device.data.gps);
                            let isMoving = false;
                            let isStopped = false;

                            if (hasData) {
                                lat = device.data.gps.latitude;
                                lng = device.data.gps.longitude;
                                const isMove = device.data.io_elements?.[240].value;
                                if (isMove == '1') {
                                    isMoving = true;
                                } else {
                                    isStopped = true;
                                }
                                device.lastLat = lat;
                                device.lastLng = lng;
                            } else if (device.lastLat !== undefined && device.lastLng !== undefined) {
                                lat = device.lastLat;
                                lng = device.lastLng;
                                isStopped = true; // Asumimos que está parado si usamos la última posición conocida
                            } else {
                                return null; // No renderizar el marcador si no hay datos de ubicación
                            }

                            let bgColor = '#e0e0e0'; // gris claro por defecto
                            if (hasData) {
                                if (isMoving) {
                                    bgColor = '#b9fbc0'; // verde claro
                                } else if (isStopped) {
                                    bgColor = '#ffb3b3'; // rojo claro
                                }
                            }
                            const customIcon = L.divIcon({
                                html: `<div style="font-size: 10px; font-weight: 600; background-color: ${bgColor}; border-radius: 5px; padding: 2px 5px; border: 1px solid black; white-space: nowrap; display: inline-block;">${device.vehicle?.plate ?? ''}</div>`,

                                className: 'mt-[20px]',
                                iconAnchor: [10, 30]
                            });
                            if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) return null;
                            return (
                                <Marker
                                    key={device.vehicle?.id || `marker-${index}`}
                                    position={[lat, lng]}
                                    // @ts-ignore
                                    icon={customIcon as any}
                                    eventHandlers={{
                                        click: () => setSelectedDevice(device),
                                    }}
                                >
                                    {/* <Popup>
                                    <div className="text-[13px] font-semibold">{device.vehicle?.plate ?? ''}</div>
                                    <div className='text-[13px] font-semibold'>{t('Speed')} <span className='text-[13px] font-normal'>{device?.data?.io_elements?.[24]?.value} Km/h</span></div>
                                </Popup> */}
                                </Marker>
                            );

                        })}
                    </MapContainer>
                </div> {/* cierra el div de style={{ height: 'calc(100vh - 140px)' }} */}
                {/* Div lateral para mostrar datos del device seleccionado */}

            </div>
        </PageBody>
    );
};
